import React, { useEffect, useState } from 'react';
import API from './api';

const statusColors = {
  not_achieved: 'bg-red-200',
  emerging: 'bg-yellow-200',
  achieved: 'bg-green-200'
};

function MyStudentsSkills() {
  const [data, setData] = useState([]);
  const [workingSkills, setWorkingSkills] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [selectedSkillName, setSelectedSkillName] = useState('');
  const [selectedStudentName, setSelectedStudentName] = useState('');
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [monthFilter, setMonthFilter] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const res = await API.get('/my-students-skills');
      const { students_skills = [], available_months = [] } = res.data;

      setData(students_skills);
      setAvailableMonths(available_months);

      const init = {};
      students_skills.forEach(({ student, skills }) => {
        const byDomain = {};
        skills.forEach(skill => {
          if (skill.is_baseline) {
            byDomain[skill.domain] = [];
          }
        });
        Object.keys(byDomain).forEach(domain => {
          init[`${student.id}_${domain}`] = [];
        });
      });
      setWorkingSkills(init);
    } catch (err) {
      alert('Failed to load student skills');
    }
  };

  const updateSkillWithNote = async () => {
    if (!pendingUpdate) return;
    try {
      await API.post(`/students/${pendingUpdate.student_id}/skills/${pendingUpdate.skill_id}`, {
        status: pendingUpdate.newStatus,
        is_baseline: pendingUpdate.is_baseline || false,
        note: pendingUpdate.note || noteInput
      });
      setShowNoteModal(false);
      setPendingUpdate(null);
      await fetchAll();
    } catch (err) {
      alert('Update failed');
    }
  };

  const openHistory = async (student_id, skill_id, skillName, studentName) => {
    try {
      const res = await API.get(`/students/${student_id}/skills/${skill_id}/history`);
      setHistoryData(res.data);
      setSelectedSkillName(skillName);
      setSelectedStudentName(studentName);
      setShowHistory(true);
    } catch (err) {
      alert('Failed to load history');
    }
  };

  const addWorkingSkill = (studentId, domain, skillId) => {
    if (!skillId) return;
    const key = `${studentId}_${domain}`;
    setWorkingSkills(prev => ({
      ...prev,
      [key]: [...(prev[key] || []), parseInt(skillId)]
    }));
  };

  return (
    <div className="p-1">
      <h2 className="text-lg font-bold mb-4">My Students Skills</h2>

      <div className="flex gap-6 items-center mb-4">
        <div>
          <label className="mr-2 font-semibold">Select student:</label>
          <select
            className="border px-2 py-1 rounded text-sm"
            value={selectedStudentId}
            onChange={e => setSelectedStudentId(e.target.value)}
          >
            <option value="">-- Choose --</option>
            {data.map(({ student }) => (
              <option key={student.id} value={student.id}>
                {student.first_name} {student.last_name}
              </option>
            ))}
          </select>
        </div>

        {selectedStudentId && availableMonths.length > 0 && (
          <div>
            <label className="mr-2 font-semibold">Filter by month:</label>
            <select
              className="border px-2 py-1 rounded text-sm"
              value={monthFilter}
              onChange={e => setMonthFilter(e.target.value)}
            >
              <option value="">All</option>
              {availableMonths.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {selectedStudentId &&
        data
          .filter(({ student }) => student.id.toString() === selectedStudentId)
          .map(({ student, skills }) => {
            const groupedByDomain = {};
            skills.forEach(skill => {
              if (!groupedByDomain[skill.domain]) groupedByDomain[skill.domain] = [];
              groupedByDomain[skill.domain].push(skill);
            });

            return (
              <div key={student.id} className="mb-8 border rounded p-4 shadow">
                <h3 className="font-semibold text-lg mb-2">
                  {student.first_name} {student.last_name}
                </h3>

                {Object.entries(groupedByDomain).map(([domain, domainSkills]) => {
                  const baselineSkill = domainSkills.find(s => s.is_baseline);
                  const hasBaseline = !!baselineSkill;
                  const postBaselineSkills = domainSkills.filter(
                    s => baselineSkill && s.position_order > baselineSkill.position_order
                  );
                  const key = `${student.id}_${domain}`;
                  const addedSkills = workingSkills[key] || [];

                  const filteredSkills = domainSkills.filter(skill => {
                    if (!monthFilter) return true;
                    return skill.assessed_at?.startsWith(monthFilter);
                  });

                  const activeSkills = filteredSkills.filter(
                    s => baselineSkill && s.position_order > baselineSkill.position_order &&
                    ['not_achieved', 'emerging', 'achieved'].includes(s.status)
                  );

                  const currentWorking = [
                    ...activeSkills,
                    ...postBaselineSkills.filter(
                      s => addedSkills.includes(s.id) && !activeSkills.some(a => a.id === s.id)
                    )
                  ];

                  return (
				<div key={domain} className="mb-6">
				  <div className="font-semibold uppercase mb-2">{domain.replace('_', ' ')}</div>

				  {/* Set Baseline blok */}
				  {!hasBaseline ? (
				    <div className="flex flex-col items-stretch gap-2 w-full max-w-md mx-auto mb-3">
				      <select
				        className="border rounded px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
				        defaultValue=""
				        id={`baseline-${student.id}-${domain}`}
				      >
				        <option value="">Select Baseline</option>
				        {domainSkills.map(s => (
				          <option key={s.id} value={s.id}>
				            {s.age_range} | {s.code} – {s.name}
				          </option>
				        ))}
				      </select>
				      <button
				        className="px-3 py-2 bg-blue-500 text-white rounded text-sm w-full"
				        onClick={async () => {
				          const select = document.getElementById(`baseline-${student.id}-${domain}`);
				          const selectedId = parseInt(select.value);
				          if (selectedId) {
				            try {
				              await API.post(`/students/${student.id}/skills/${selectedId}`, {
				                status: 'not_achieved',
				                is_baseline: true
				              });
				              await fetchAll();
				            } catch (err) {
				              alert('Baseline set failed');
				            }
				          }
				        }}
				      >
				        Set Baseline
				      </button>
				    </div>
				  ) : (
				    <div className="mb-3">
				      <span className="text-green-600 font-semibold">
				        Baseline set: {baselineSkill.code} – {baselineSkill.name}
				      </span>
				    </div>
				  )}

				  {/* Add Skill blok */}
				  {hasBaseline && (!monthFilter || monthFilter === availableMonths[0]) && (
				    <div className="flex flex-col items-stretch gap-2 w-full max-w-md mx-auto mb-3">
				      <select
				        className="border rounded px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-400"
				        id={`add-skill-${student.id}_${domain}`}
				      >
				        <option value="">Add skill...</option>
				        {postBaselineSkills.map(s => (
				          <option key={s.id} value={s.id}>
				            {s.age_range} | {s.code} – {s.name}
				          </option>
				        ))}
				      </select>
				      <button
				        className="px-3 py-2 bg-indigo-500 text-white rounded text-sm w-full"
				        onClick={() => {
				          const select = document.getElementById(`add-skill-${student.id}_${domain}`);
				          const selectedId = parseInt(select.value);
				          addWorkingSkill(student.id, domain, selectedId);
				          select.value = '';
				        }}
				      >
				        Add Skill
				      </button>
				    </div>
				  )}

				  {/* Table of skills remains unchanged */}
				  {hasBaseline && currentWorking.length > 0 && (
				    <div className="overflow-x-auto">
				      <table className="w-full table-auto text-sm border mt-2">
				        <thead>
				          <tr className="bg-gray-100">
				            <th className="p-2">Code</th>
				            <th className="p-2">Skill</th>
				            <th className="p-2">Status</th>
				            <th className="p-2">Actions</th>
				          </tr>
				        </thead>
				        <tbody>
				          {currentWorking.map(skill => {
				            const isAchieved = skill?.status === 'achieved';
				            return (
				              <tr key={skill.id} className="border-b">
				                <td className="p-2 text-center">
				                  {skill.code}
				                  <div className="text-xs text-gray-500">{skill.age_range}</div>
				                </td>
				                <td className="p-2">{skill.name}</td>
				                <td className={`p-2 ${statusColors[skill.status] || ''}`}>{skill.status || '–'}</td>
				                <td className="p-2 space-x-1">
				                  {!isAchieved && ['not_achieved', 'emerging', 'achieved'].map(status => (
				                    <button
				                      key={status}
				                      className={`px-2 py-1 text-xs rounded ${skill.status === status ? 'bg-gray-400 text-white' : 'bg-blue-200'}`}
				                      onClick={() => {
				                        setPendingUpdate({
				                          student_id: student.id,
				                          skill_id: skill.id,
				                          newStatus: status
				                        });
				                        setNoteInput('');
				                        setShowNoteModal(true);
				                      }}
				                    >
				                      {status.replace('_', ' ')}
				                    </button>
				                  ))}
				                  <button
				                    className="ml-2 px-2 py-1 text-xs rounded border border-gray-400 text-gray-700"
				                    onClick={() => openHistory(student.id, skill.id, skill.name, `${student.first_name} ${student.last_name}`)}
				                  >
				                    History
				                  </button>
				                </td>
				              </tr>
				            );
				          })}
				        </tbody>
				      </table>
				    </div>
				  )}
				</div>
                    
                  );
                })}
              </div>
            );
          })}

      {showNoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">Add Note</h2>
            <textarea
              rows={4}
              className="w-full border rounded p-2 text-sm"
              placeholder="Optional notes..."
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
            />
            <div className="mt-4 flex justify-end space-x-2">
              <button onClick={() => setShowNoteModal(false)} className="px-4 py-1 bg-gray-300 rounded">Cancel</button>
              <button onClick={updateSkillWithNote} className="px-4 py-1 bg-blue-500 text-white rounded">Save</button>
            </div>
          </div>
        </div>
      )}

	  {showHistory && (
	    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
	      <div className="bg-white p-3 sm:p-6 rounded w-[98%] max-w-xl max-h-[90vh] overflow-y-auto shadow-lg">
	        <h2 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">
	          History: {selectedSkillName} for {selectedStudentName}
	        </h2>
	        <div className="overflow-x-auto mb-2">
	          <table className="w-full text-xs sm:text-sm mb-3">
	            <thead>
	              <tr className="bg-gray-100">
	                <th className="p-2">Date</th>
	                <th className="p-2">From</th>
	                <th className="p-2">To</th>
	                <th className="p-2">Note</th>
	                <th className="p-2">Evaluator</th>
	              </tr>
	            </thead>
	            <tbody>
	              {historyData.map((entry, idx) => (
	                <tr key={idx} className="border-b">
	                  <td className="p-2 whitespace-nowrap">{new Date(entry.changed_at).toLocaleString()}</td>
	                  <td className="p-2">{entry.previous_status || '–'}</td>
	                  <td className="p-2">{entry.new_status}</td>
	                  <td className="p-2">{entry.note || ''}</td>
	                  <td className="p-2">{entry.substitute_name || entry.teacher_name || '–'}</td>
	                </tr>
	              ))}
	            </tbody>
	          </table>
	        </div>
	        <div className="flex justify-end">
	          <button onClick={() => setShowHistory(false)} className="px-4 py-2 bg-gray-200 rounded">Close</button>
	        </div>
	      </div>
	    </div>
	  )}
      
    </div>
  );
}

export default MyStudentsSkills;





